import {
  Banner,
  Button,
  Icon,
  InputSelectionList,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import { useCallback, useEffect, useState } from 'react';
import { FieldValues, useFieldArray, useForm } from 'react-hook-form';
import { useDebounce } from 'react-use';

import {
  customFieldsApi,
  CustomFieldSelectAttribute,
  CustomFieldStringAttributeValue,
  CustomFieldStringListAttributeValue,
  CustomFieldTextAttribute,
} from 'apis/customFields';
import {
  BaseCustomFieldSelect,
  BaseCustomFieldText,
  errorNotification,
  successNotification,
} from 'components/customFields';
import { useNotifications } from 'hooks/useNotifications';
import { CustomField, useCustomFields } from 'utils/customFields';
import { createAppModal } from 'utils/modal';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './addContactAttributesModal.module.scss';
import { ContactAttributeFormField } from './components/contactAttributeFormField/contactAttributeFormField';

type TextCustomField = CustomField<
  CustomFieldTextAttribute,
  CustomFieldStringAttributeValue
>;

type SelectCustomField = CustomField<
  CustomFieldSelectAttribute,
  CustomFieldStringListAttributeValue
>;

type FieldNewValue = CustomField & { newValue?: string | string[][] };

const formId = 'add-contact-attributes-form';

const searchIntervalMs = 300;

export const AddContactAttributesModal = createAppModal(
  { type: 'contact:addContactAttributes' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const { user } = modal.arg;

    const hasManageCustomAttributesAccess = useHasAccess(
      'settings.custom_attribute',
    );

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    useDebounce(() => setDebouncedSearchTerm(searchTerm), searchIntervalMs, [
      searchTerm,
    ]);
    const notify = useNotifications();
    const navigate = useAppNavigate();
    const { customFields, asyncStatus } = useCustomFields({
      entityId: 'contact',
      entityInstanceId: user.id,
    });

    const { control, formState, handleSubmit } = useForm();
    const {
      append,
      fields: _fields,
      remove,
      replace,
    } = useFieldArray({
      control,
      name: formId,
    });
    const fields = _fields as unknown as CustomField[];

    const [updateValue, { isLoading }] =
      customFieldsApi.useUpdateValueMutation();
    const onSubmit = useCallback(
      async (data: FieldValues) => {
        const form: FieldNewValue[] = data[formId];

        let isPatchEmpty = true;

        // Filter out deleted fields which used to have value in the API
        const deletedFields = customFields.filter(
          ({ attribute: { id }, value }) =>
            value && !form.some(({ attribute }) => attribute.id === id),
        );
        const patch = form.concat(deletedFields).reduce(
          (
            body,
            { attribute: { id, type, data_type }, newValue, value: oldValue },
          ) => {
            // Select field component wraps result in an array
            const value = type === 'select' ? newValue?.at(0) : newValue;

            if (isEqual(oldValue?.value, value)) {
              return body;
            }

            if (oldValue) {
              isPatchEmpty = false;
              body.before[id] = { data_type, value: oldValue.value };
            }

            if (value) {
              isPatchEmpty = false;
              body.after[id] = { data_type, value };
            }

            // The attribute value is either deleted or updated
            if (oldValue || value) {
              ampli.customAttributeValueUpdated({
                entityId: 'contact',
                entityInstanceId: user.id,
                attributeId: id,
                type,
              });
            }

            return body;
          },
          { before: {}, after: {} },
        );

        if (isPatchEmpty) {
          hide();
          return;
        }

        const response = await updateValue({
          patch,
          entityId: 'contact',
          entityInstanceId: user.id,
        });

        if (isResponseWithData(response)) {
          notify(successNotification);
          hide();
        } else {
          notify(errorNotification);
        }
      },
      [customFields, notify, hide, updateValue, user.id],
    );

    const getOrMakeNewValue = ({ attribute, value }: CustomField) =>
      attribute.type === 'text'
        ? value?.value ?? ''
        : value
        ? [value.value]
        : [];

    // Filter out and display only empty, not already selected fields for enduser to edit.
    const availableFields = customFields.filter(
      ({ value, attribute: { id } }) =>
        !value && !fields.some(({ attribute }) => attribute.id === id),
    );

    const noAttributes = !customFields.length && asyncStatus === 'fulfilled';

    useEffect(() => {
      // Add all non-empty fields
      replace(
        customFields
          .filter(({ value }) => value)
          .map((f) => ({
            ...f,
            newValue: getOrMakeNewValue(f),
          })),
      );
    }, [replace, customFields]);

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead title="Edit contact attributes" onClose={hide} />

        <ModalBody>
          {noAttributes ? (
            <Banner
              variant="warning"
              headline="No attributes to edit"
              actionSlot={
                hasManageCustomAttributesAccess && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      hide();
                      navigate({ path: '/settings/custom-attributes/contact' });
                    }}
                  >
                    Add attributes in settings
                  </Button>
                )
              }
              description={
                <Text.Paragraph>
                  There are no active contact attributes in your organization.
                </Text.Paragraph>
              }
            />
          ) : (
            <form id={formId} onSubmit={handleSubmit(onSubmit)}>
              {fields.map((field, index) => {
                const name = `${formId}.${index}.newValue`;
                const onRemove = () => remove(index);

                const { type } = field.attribute;
                if (type === 'text') {
                  return (
                    <ContactAttributeFormField
                      key={field.attribute.id}
                      onClick={onRemove}
                    >
                      <BaseCustomFieldText
                        customField={field as TextCustomField}
                        control={control}
                        name={name}
                      />
                    </ContactAttributeFormField>
                  );
                }

                if (type === 'select') {
                  return (
                    <ContactAttributeFormField
                      key={field.attribute.id}
                      onClick={onRemove}
                    >
                      <BaseCustomFieldSelect
                        customField={field as SelectCustomField}
                        control={control}
                        name={name}
                      />
                    </ContactAttributeFormField>
                  );
                }

                return null;
              })}
              <Tooltip
                className={styles.attributeDropdown}
                content={
                  availableFields.length === 0
                    ? 'No more attributes to add'
                    : ''
                }
              >
                <InputSelectionList
                  // Disable dropdown if all empty fields were already added
                  disabled={availableFields.length === 0}
                  placeholder="Select attribute"
                  getItemTitleByValue={(
                    field: CustomField | null | undefined,
                  ) => field?.attribute.label}
                  filter={{
                    name: 'contactFieldSearchQuery',
                    value: searchTerm,
                    onChange: (e) => setSearchTerm(e.target.value),
                  }}
                  onToggleDropdown={(isOpen) => {
                    if (!isOpen) {
                      setSearchTerm('');
                    }
                  }}
                  onChange={(event) => {
                    if (event.selectedItem?.value) {
                      const { attribute } = event.selectedItem.value;
                      append({
                        attribute,
                        newValue: getOrMakeNewValue(event.selectedItem.value),
                      });
                    }
                  }}
                  prefix={{
                    type: 'custom',
                    value: <Icon name="input" />,
                  }}
                >
                  {availableFields
                    // Apply search filter
                    .filter(({ attribute: { label } }) =>
                      label
                        .toLowerCase()
                        .includes(debouncedSearchTerm.trim().toLowerCase()),
                    )
                    .map((field) => (
                      <SelectionList.Item
                        key={field.attribute.id}
                        value={field}
                      >
                        <Text.Paragraph>{field.attribute.label}</Text.Paragraph>
                      </SelectionList.Item>
                    ))}
                </InputSelectionList>
              </Tooltip>
            </form>
          )}
        </ModalBody>
        <ModalFoot
          cancelOnClick={hide}
          confirmName="Save"
          confirmOnClick={noop}
          confirmProps={{
            isLoading,
            type: 'submit',
            form: formId,
            disabled: noAttributes || formState.isSubmitting,
            loadingIndicator: 'Saving...',
          }}
        />
      </Modal>
    );
  },
);
